import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import Login from '@/pages/Login';
import CustomerRegister from '@/pages/CustomerRegister';
import CustomerPortal from '@/pages/CustomerPortal';
import CustomerProfile from '@/pages/CustomerProfile';
import CustomerProjectDetail from '@/pages/CustomerProjectDetail';
import Dashboard from '@/pages/Dashboard';
import CRM from '@/pages/CRM';
import AISettings from '@/pages/AISettings';
import EstimateBuilder from '@/pages/EstimateBuilder';
import EstimateManager from '@/pages/EstimateManager';
import CostingSetup from '@/pages/CostingSetup';
import BurnRateManager from '@/pages/BurnRateManager';
import PurchaseOrders from '@/pages/PurchaseOrders';
import WorkCalendar from '@/pages/WorkCalendar';
import ProductionManager from '@/pages/ProductionManager';
import QuoteBuilder from '@/pages/QuoteBuilder';
import QuoteManager from '@/pages/QuoteManager';
import InvoiceManager from '@/pages/InvoiceManager';
import ProjectTracker from '@/pages/ProjectTracker';
import ProjectDetail from '@/pages/ProjectDetailEnhanced';
import WorkBoard from '@/pages/WorkBoard';
import WorkBoardProjectDetail from '@/pages/WorkBoardProjectDetail';
import MonthlyPerformance from '@/pages/MonthlyPerformance';
import CompanySettings from '@/pages/CompanySettings';
import SecurityModule from '@/pages/SecurityModule';
import Tools from '@/pages/Tools';
import QCSheets from '@/pages/tools/QCSheets';
import QCSheetDetail from '@/pages/tools/QCSheetDetail';
import QCSheetTemplateBuilder from '@/pages/tools/QCSheetTemplateBuilder';
import Checklists from '@/pages/tools/Checklists';
import ChecklistDetail from '@/pages/tools/ChecklistDetail';
import ChecklistTemplateBuilder from '@/pages/tools/ChecklistTemplateBuilder';
import RFQManager from '@/pages/tools/RFQManager';
import Inventory from '@/pages/tools/Inventory';
import DecisionTree from '@/pages/tools/DecisionTree';
import BugReports from '@/pages/BugReports';
import LabourTracking from '@/pages/LabourTracking';
import ConversationalEstimate from '@/pages/ConversationalEstimate';
import AnalyticsDashboard from '@/pages/AnalyticsDashboard';
import SchedulingBoard from '@/pages/SchedulingBoard';
import BillingManager from '@/pages/BillingManager';
import ReportsExport from '@/pages/ReportsExport';
import Layout from '@/components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  // Determine redirect path based on role
  const getDefaultPath = () => {
    if (!user) return '/login';
    return user.role === 'Customer' ? '/portal' : '/';
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to={getDefaultPath()} />}
          />
          <Route
            path="/register"
            element={!user ? <CustomerRegister onLogin={handleLogin} /> : <Navigate to={getDefaultPath()} />}
          />
          
          {/* Customer Portal Route */}
          <Route
            path="/portal"
            element={
              user && user.role === 'Customer' ? (
                <CustomerPortal user={user} onLogout={handleLogout} />
              ) : user ? (
                <Navigate to="/" />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          
          {/* Customer Profile Route */}
          <Route
            path="/profile"
            element={
              user && user.role === 'Customer' ? (
                <CustomerProfile />
              ) : user ? (
                <Navigate to="/" />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          
          {/* Customer Project Detail Route */}
          <Route
            path="/project/:projectId"
            element={
              user && user.role === 'Customer' ? (
                <CustomerProjectDetail user={user} />
              ) : user ? (
                <Navigate to="/" />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          
          {/* Customer Estimate Builder Route */}
          <Route
            path="/estimate-request"
            element={
              user && user.role === 'Customer' ? (
                <EstimateBuilder user={user} />
              ) : user ? (
                <Navigate to="/" />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          
          {/* Conversational AI Estimate Route */}
          <Route
            path="/estimate-chat"
            element={
              user && user.role === 'Customer' ? (
                <ConversationalEstimate user={user} />
              ) : user ? (
                <Navigate to="/" />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          
          {/* Admin/Staff Routes - Protected from Customers */}
          <Route
            path="/"
            element={
              user ? (
                user.role === 'Customer' ? (
                  <Navigate to="/portal" />
                ) : (
                  <Layout user={user} onLogout={handleLogout} />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="workboard" element={<WorkBoard user={user} />} />
            <Route path="workboard/projects/:projectId" element={<WorkBoardProjectDetail user={user} />} />
            <Route path="crm" element={<CRM />} />
            <Route path="estimates" element={<EstimateManager />} />
            <Route path="settings/ai" element={<AISettings />} />
            <Route path="costing" element={<CostingSetup />} />
            <Route path="burn-rate" element={<BurnRateManager />} />
            <Route path="purchase-orders" element={<PurchaseOrders />} />
            <Route path="work-calendar" element={<WorkCalendar />} />
            <Route path="production-manager" element={<ProductionManager />} />
            <Route path="quote-manager" element={<QuoteManager />} />
            <Route path="quote-builder" element={<QuoteBuilder />} />
            <Route path="invoices" element={<InvoiceManager />} />
            <Route path="projects" element={<ProjectTracker />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="performance" element={<MonthlyPerformance />} />
            <Route path="settings" element={<CompanySettings />} />
            <Route path="security" element={<SecurityModule />} />
            <Route path="tools" element={<Tools />} />
            <Route path="tools/qc-sheets" element={<QCSheets />} />
            <Route path="qc-sheets/:sheetId" element={<QCSheetDetail />} />
            <Route path="qc-sheets/template-builder/:sheetType" element={<QCSheetTemplateBuilder />} />
            <Route path="tools/checklists" element={<Checklists />} />
            <Route path="checklists/:sheetId" element={<ChecklistDetail />} />
            <Route path="checklists/template-builder/:sheetType" element={<ChecklistTemplateBuilder />} />
            <Route path="tools/rfq" element={<RFQManager />} />
            <Route path="tools/inventory" element={<Inventory />} />
            <Route path="tools/decision-tree" element={<DecisionTree />} />
            <Route path="bug-reports" element={<BugReports />} />
            <Route path="labour-tracking" element={<LabourTracking />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="scheduling" element={<SchedulingBoard />} />
            <Route path="billing" element={<BillingManager />} />
            <Route path="reports" element={<ReportsExport />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
